import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ExternalLink, Home, Link2, MousePointerClick } from 'lucide-react';
import api from '../services/api';
import { NavSectorItems } from '../components/M45NavPills';
import { getPageZoom } from '../utils/desktopZoomFix';
import { useV3Prefs } from './V3PrefsContext';
import V3Avatar from './V3Avatar';
import { CollapseIcon, Pin, MenuIcon } from './V3Icons';
import { ENCON_SPHERE_SRC } from './v3Sphere';
import V3BarTip from './V3BarTip';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Lifts a colour towards white so group colours stay readable on dark glass.
const onDark = (color, fallback) => `color-mix(in srgb, ${color || fallback} 68%, white)`;

// The left edge bar. It holds the navigation tabs (the person's real Global Header: Pro Forma, Quotes, Sales / CRM, Stock Trf/Req,
// Admin ... plus Transport and the quick call note) and the quick links. Nothing else. Minimised, the SAME tabs are shown as icons: a drop-down
// tab opens its slide-out menu from the minimised bar (the bar stays minimised) and a plain link goes straight to its page.
// At the foot: the three saved views, the pin button (laptop sizes) and the collapse button.
const V3LeftBar = ({ user, overlay = false, parked = false }) => {
  const navigate = useNavigate();
  // The Home button's orange ring shows only while the person is on the Home page.
  const onHome = useLocation().pathname === '/';
  const { modes, toggleBar, barMode, setBarMode, setDefaultState, closeOverlay, theme, setPrefsOpen, prefsOpen } = useV3Prefs();
  const [quickLinks, setQuickLinks] = useState([]);
  const [linkGroups, setLinkGroups] = useState([]);
  const [sphereFailed, setSphereFailed] = useState(false);
  const mode = overlay ? 'open' : modes.left;
  const barRef = useRef(null);
  const actionsRef = useRef(null);
  const quickRef = useRef(null);
  const footRef = useRef(null);
  // Set once a drop-down tab has been pressed: its pop-out then lines up with that tab (see the effect below) and the measured fallback position is left alone.
  const anchoredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [linksRes, groupsRes] = await Promise.all([
          api.get(`${API_BASE}/api/quick-links`),
          api.get(`${API_BASE}/api/quick-link-groups`),
        ]);
        if (cancelled) return;
        setQuickLinks(Array.isArray(linksRes.data) ? linksRes.data : []);
        setLinkGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
      } catch (_) { /* the bar still shows the navigation tabs */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // The slide-out menus come out from behind this bar, from the hairline under the Sector field down to just above Quick links (or, minimised,
  // to just above the saved views). This measures that area, in the page's own pixels so it is right at any zoom, and publishes it on the bar
  // as --v3-flyout-left / -top / -height for v3.css to use.
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;
    const publish = () => {
      const zoom = getPageZoom() || 1;
      const barRect = bar.getBoundingClientRect();
      const list = actionsRef.current ? actionsRef.current.querySelector('.v3-actions') : null;
      const sectorBlock = list ? list.querySelector(':scope > div:has(> [data-testid="nav-header-sector-dropdown-trigger"])') : null;
      const top = sectorBlock ? sectorBlock.getBoundingClientRect().bottom : (list ? list.getBoundingClientRect().top : barRect.top + 120);
      bar.style.setProperty('--v3-flyout-left', `${barRect.right / zoom}px`);
      // A pop-out is as tall as its content, up to the height of the screen; the effect below lines it up with the tab that was pressed.
      bar.style.setProperty('--v3-flyout-max', `${Math.max(240, (window.innerHeight - 24) / zoom)}px`);
      if (!anchoredRef.current) bar.style.setProperty('--v3-flyout-top', `${top / zoom}px`);
    };
    publish();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    if (observer) { observer.observe(bar); if (actionsRef.current) observer.observe(actionsRef.current); }
    window.addEventListener('resize', publish);
    bar.addEventListener('transitionend', publish);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', publish);
      bar.removeEventListener('transitionend', publish);
    };
  }, [mode, overlay, quickLinks, linkGroups, user?.id]);

  // A pop-out (the panel a drop-down tab slides out) lines up with the tab that was pressed: its top edge is level with that tab. If the panel is too tall to fit
  // from there down the screen, it is moved up just far enough to fit (so only tall ones are ever moved).
  useEffect(() => {
    const holder = actionsRef.current;
    const bar = barRef.current;
    if (!holder || !bar) return undefined;
    const PANEL = '[style*="z-index: 9999"], [data-testid="log-call-dropdown-menu"]';
    let anchorTop = null;
    let watched = null;
    const place = () => {
      const panel = holder.querySelector(PANEL);
      if (!panel || anchorTop === null) return;
      const zoom = getPageZoom() || 1;
      const height = panel.getBoundingClientRect().height / zoom;
      const lowest = window.innerHeight / zoom - 12;
      bar.style.setProperty('--v3-flyout-top', `${Math.max(12, Math.min(anchorTop, lowest - height))}px`);
    };
    const resizeWatcher = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
    const onPress = (event) => {
      const target = event.target instanceof Element ? event.target.closest('.v3-actions button, .v3-actions a') : null;
      if (!target || target.closest(PANEL)) return;
      anchorTop = target.getBoundingClientRect().top / (getPageZoom() || 1);
      anchoredRef.current = true;
      requestAnimationFrame(place);
    };
    const mutationWatcher = new MutationObserver(() => {
      const panel = holder.querySelector(PANEL);
      if (panel === watched) return;
      if (resizeWatcher && watched) resizeWatcher.unobserve(watched);
      watched = panel;
      if (panel) { if (resizeWatcher) resizeWatcher.observe(panel); place(); }
    });
    holder.addEventListener('click', onPress, true);
    mutationWatcher.observe(holder, { childList: true, subtree: true });
    window.addEventListener('resize', place);
    return () => {
      holder.removeEventListener('click', onPress, true);
      mutationWatcher.disconnect();
      if (resizeWatcher) resizeWatcher.disconnect();
      window.removeEventListener('resize', place);
    };
  }, [mode, overlay]);

  if (mode === 'hidden') {
    return (
      <button
        type="button"
        className="v3-handle v3-handle--left"
        onClick={() => toggleBar('left')}
        aria-label="Open navigation"
        data-testid="v3-left-handle"
      >
        <CollapseIcon side="left" open={false} />
      </button>
    );
  }

  const className = [
    'v3-glass', 'v3-glass--flat', 'v3-bar', 'v3-bar--left',
    mode === 'strip' ? 'v3-bar--strip' : '',
    overlay ? 'v3-bar--overlay' : '',
    parked ? 'v3-bar--parked' : '',
  ].filter(Boolean).join(' ');

  const linksForGroup = (groupId) =>
    quickLinks.filter((link) => (link.group_id || 'default') === groupId).sort((a, b) => (a.order || 0) - (b.order || 0));
  const filledGroups = linkGroups.filter((group) => linksForGroup(group.id).length > 0);

  const openMenu = () => toggleBar('left');
  // The two buttons at the foot: Pinned and Closes-when-I-click-away. A button that is lit is the way this bar is working now; pressing it MINIMISES the bar (it keeps working that
  // way, and a pinned bar remembers it is minimised). It never changes how the bar works, so a pinned bar stays pinned. Pressing the one that is not lit makes the bar work that way
  // (a bar that is open over the page is then pinned or closes-when-I-click-away: part of the page). 'Over the page' is chosen in My preferences.
  const pinnedNow = barMode.left === 'pinned' && !overlay;
  const autoNow = barMode.left === 'auto' && !overlay;

  // The user circle: the person's photo (or initials). It opens My preferences (profile, password, colour scheme, and how the side bars start). It has an orange
  // outline only while My preferences is open. When the bar is open it sits at the top, between the orange circle and the burger menu; when the bar is minimised
  // it sits at the bottom of the strip.
  const userCircle = (
    <button
      type="button"
      className={`v3-user-circle ${prefsOpen ? 'v3-user-circle--on' : ''}`}
      onClick={() => { setPrefsOpen(true); if (overlay) closeOverlay(); }}
      aria-label="My preferences"
      data-v3-tip="My preferences"
      data-testid="v3-user-circle"
    >
      <V3Avatar user={user} size={36} />
    </button>
  );

  return (
    <nav ref={barRef} className={className} data-testid={overlay ? 'v3-left-bar-overlay' : 'v3-left-bar'} aria-label="Navigation">
      <V3BarTip barRef={barRef} side="right" />
      <div className="v3-bar-mark">
        <button
          type="button"
          className={`v3-mark-button ${onHome ? 'v3-mark-button--on' : ''}`}
          onClick={() => { navigate('/'); if (overlay) closeOverlay(); }}
          title="Home"
          aria-label="Home"
          data-testid="v3-home-mark"
        >
          {sphereFailed
            ? <span className="v3-sphere-fallback" aria-hidden="true" />
            : <img src={ENCON_SPHERE_SRC} alt="Encon" width="32" height="32" onError={() => setSphereFailed(true)} />}
          {/* A house over the orange sphere: this is the Home button. */}
          <Home className="v3-mark-home" aria-hidden="true" />
        </button>
        {mode !== 'strip' && userCircle}
        <button
          type="button"
          className="v3-burger"
          onClick={() => toggleBar('left')}
          title={mode === 'strip' ? 'Open navigation' : 'Close navigation'}
          aria-label={mode === 'strip' ? 'Open navigation' : 'Close navigation'}
          aria-expanded={mode !== 'strip'}
          data-testid="v3-left-burger"
        >
          <MenuIcon className="v3-i" />
        </button>
      </div>

      {mode === 'strip' ? (
        <div className="v3-bar-body v3-bar-body--strip">
          <div className="v3-actions-wrap" ref={actionsRef} data-testid="v3-global-actions-strip">
            {/* Same children, in the same order, as the open bar (the title is hidden by the stylesheet here): the tabs are never rebuilt when the bar folds
                or opens, so a window opened from a tab (Create Quote, and its Price Lists panel making the bars fold) is not closed by it. */}
            <div className="v3-nav-label">Navigation</div>
            <NavSectorItems user={user} />
          </div>
          <div className="v3-bar-scroll v3-bar-scroll--strip">
            {filledGroups.length > 0 && (
              <button
                type="button"
                className="v3-nav-item"
                onClick={openMenu}
                title="Quick links"
                aria-label="Quick links"
                data-testid="v3-strip-quick-links"
              >
                <span className="v3-nav-chip"><Link2 className="v3-i" /></span>
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="v3-bar-body">
          <div className="v3-actions-wrap" ref={actionsRef} data-testid="v3-global-actions">
            <div className="v3-nav-label">Navigation</div>
            <NavSectorItems user={user} />
          </div>

          <div className="v3-bar-scroll" ref={quickRef}>
            {filledGroups.length > 0 && (
              <>
                <div className="v3-nav-label">Quick links</div>
                {filledGroups.map((group) => (
                  <div key={group.id} className="v3-linkgroup">
                    <p className="v3-linkgroup-name" style={{ color: theme === 'dark' ? onDark(group.color, '#a78bfa') : (group.color || '#6d28d9') }}>{group.name}</p>
                    {linksForGroup(group.id).map((link) => (
                      <a
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="v3-linkrow"
                        style={{ color: theme === 'dark' ? onDark(group.color, '#a78bfa') : (group.color || '#6d28d9') }}
                        data-testid={`v3-quick-link-${link.id}`}
                      >
                        <ExternalLink className="v3-i v3-i--sm" />
                        <span>{link.name}</span>
                      </a>
                    ))}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      <div ref={footRef} className={`v3-bar-foot ${mode === 'strip' ? 'v3-bar-foot--strip' : ''}`}>
        {/* How the bar works: pinned (open, part of the page, stays), or closes by itself when you click away (open and part of the page, folds when you click outside
            it). With neither on, the bar is an icon strip that opens over the page. */}
        {mode !== 'strip' && (
          <>
            <button
              type="button"
              className={`v3-collapse ${pinnedNow ? 'v3-collapse--on' : ''}`}
              onClick={pinnedNow ? () => toggleBar('left') : () => { setBarMode('left', 'pinned'); setDefaultState('left', 'open'); }}
              aria-label="Pin navigation"
              aria-pressed={pinnedNow}
              data-v3-tip={pinnedNow ? 'Pinned: stays open and moves the page. Click to minimise it (it stays pinned)' : 'Pin: keep it open and moving the page'}
              data-testid="v3-left-pin"
            >
              <Pin className="v3-i v3-i--sm" />
            </button>
            <button
              type="button"
              className={`v3-collapse ${autoNow ? 'v3-collapse--on' : ''}`}
              onClick={autoNow ? () => toggleBar('left') : () => setBarMode('left', 'auto')}
              aria-label="Close navigation when I click away"
              aria-pressed={autoNow}
              data-v3-tip={autoNow ? 'Closes when you click away. Click to minimise it' : 'Open as part of the page, and close by itself when I click away'}
              data-testid="v3-left-auto"
            >
              <MousePointerClick className="v3-i v3-i--sm" />
            </button>
          </>
        )}
        {mode === 'strip' && userCircle}
      </div>
    </nav>
  );
};

export default V3LeftBar;
